/**
 * CaseReplyDraftsPanel — Phase 4, AI-assisted client replies (review-before-send).
 *
 * Lloyd's ask: drafted messages "from which we can just hit send (or alter as needed)". The one hard
 * rule is that nothing reaches a client without a person approving it, so this panel is deliberately
 * two steps — pick a suggestion, then review and send — and never one.
 *
 * The blanks matter. Where the backend could not fill a value (a fee Lloyd has not confirmed, an
 * offer term that lives only in the court's email) it leaves a conspicuous [ — ]. We surface those
 * loudly and refuse the one-click send, because the failure mode we are actually designing against
 * is a reviewer skimming a fluent-looking draft and sending a wrong number to a client.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplyDraftApi, {
  ReplyDraftDto,
  ReplySuggestionDto,
  DRAFT_BLANK,
} from '../../api/ReplyDraftApi';
import {
  REFERRAL_SOURCES,
  REFERRAL_SOURCE_LABELS,
} from '../../constants/referralSources';

interface CaseReplyDraftsPanelProps {
  Ticket_guid: string;
}

const CaseReplyDraftsPanel: React.FC<CaseReplyDraftsPanelProps> = ({ Ticket_guid }) => {
  const [suggestions, setSuggestions] = useState<ReplySuggestionDto[]>([]);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<ReplyDraftDto[]>([]);
  const [source, setSource] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [busyGuid, setBusyGuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** Body edits, keyed by draft guid, before they're saved. */
  const [edits, setEdits] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!Ticket_guid) return;
    setLoading(true);
    setError(null);
    try {
      const [suggested, existing] = await Promise.all([
        ReplyDraftApi.getSuggestions(Ticket_guid),
        ReplyDraftApi.listDrafts(Ticket_guid),
      ]);
      setSuggestions(suggested.suggestions);
      setBlockers(suggested.blockers);
      setSource(suggested.source ?? '');
      setDrafts(existing);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load replies');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = drafts.filter((d) => d.MessageDraft_Status === 'PENDING_REVIEW');
  const resolved = drafts.filter((d) => d.MessageDraft_Status !== 'PENDING_REVIEW');

  const bodyOf = (draft: ReplyDraftDto): string =>
    edits[draft.MessageDraft_guid] ?? draft.MessageDraft_Body;

  const handleSourceChange = async (next: string) => {
    const previous = source;
    setSource(next); // optimistic — the dropdown should not lag behind the click
    setError(null);
    try {
      await ReplyDraftApi.setReferralSource(Ticket_guid, next || null);
      await load(); // pricing and the available templates both change with the source
    } catch (err) {
      setSource(previous);
      setError(err instanceof Error ? err.message : 'Failed to set the referral source');
    }
  };

  const handleUse = async (templateKey: string) => {
    setBusyGuid(templateKey);
    setError(null);
    try {
      await ReplyDraftApi.createDraft(Ticket_guid, templateKey);
      setNotice('Draft added below for review. It has not been sent.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the draft');
    } finally {
      setBusyGuid(null);
    }
  };

  const handleSave = async (draft: ReplyDraftDto) => {
    const body = bodyOf(draft);
    if (body === draft.MessageDraft_Body) return;

    setBusyGuid(draft.MessageDraft_guid);
    setError(null);
    try {
      await ReplyDraftApi.updateDraft(draft.MessageDraft_guid, body);
      setEdits((prev) => {
        const next = { ...prev };
        delete next[draft.MessageDraft_guid];
        return next;
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the draft');
    } finally {
      setBusyGuid(null);
    }
  };

  const handleSend = async (draft: ReplyDraftDto) => {
    const body = bodyOf(draft);
    const unsaved = body !== draft.MessageDraft_Body;
    const stillBlank = body.includes(DRAFT_BLANK);

    // The server rejects a blank-bearing draft unless forced. Rather than silently forcing, make the
    // person confirm they meant it — that is the whole point of leaving the blank visible.
    if (
      stillBlank &&
      !window.confirm(
        'This draft still contains an unfilled blank ( [ — ] ). It will be sent to the client exactly ' +
          'as written. Send anyway?',
      )
    ) {
      return;
    }

    setBusyGuid(draft.MessageDraft_guid);
    setError(null);
    try {
      if (unsaved) {
        await ReplyDraftApi.updateDraft(draft.MessageDraft_guid, body);
      }
      await ReplyDraftApi.approve(draft.MessageDraft_guid, stillBlank);
      setNotice('Sent to the client.');
      setEdits((prev) => {
        const next = { ...prev };
        delete next[draft.MessageDraft_guid];
        return next;
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the draft');
    } finally {
      setBusyGuid(null);
    }
  };

  const handleDiscard = async (draft: ReplyDraftDto) => {
    setBusyGuid(draft.MessageDraft_guid);
    setError(null);
    try {
      await ReplyDraftApi.discard(draft.MessageDraft_guid);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard the draft');
    } finally {
      setBusyGuid(null);
    }
  };

  if (loading && drafts.length === 0 && suggestions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AutoAwesomeIcon fontSize="small" />
        Suggested replies
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drafts are built from the firm&apos;s standard replies and this case&apos;s details. Nothing
        is sent until you review it and press Send.
      </Typography>

      <TextField
        select
        size="small"
        label="Referral source"
        helperText="Sets this case's pricing and which replies apply."
        value={source}
        onChange={(e) => handleSourceChange(e.target.value)}
        sx={{ minWidth: 260, mb: 3 }}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
      >
        <option value="">Not set</option>
        {REFERRAL_SOURCES.map((code) => (
          <option key={code} value={code}>
            {REFERRAL_SOURCE_LABELS[code]}
          </option>
        ))}
      </TextField>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {blockers.map((blocker) => (
        <Alert severity="info" sx={{ mb: 2 }} key={blocker}>
          <AlertTitle>Some replies are unavailable</AlertTitle>
          {blocker}
        </Alert>
      ))}

      {/* ---------------------------------------------------------------- suggestions */}
      {suggestions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No standard reply fits this case&apos;s current stage.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {suggestions.map((suggestion) => (
            <Card variant="outlined" key={suggestion.templateKey}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">{suggestion.title}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {suggestion.body}
                    </Typography>
                    {suggestion.gaps.length > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        label={`${suggestion.gaps.length} thing${
                          suggestion.gaps.length === 1 ? '' : 's'
                        } to fill in`}
                      />
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busyGuid === suggestion.templateKey}
                    onClick={() => handleUse(suggestion.templateKey)}
                  >
                    Use this
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* ---------------------------------------------------------------- review queue */}
      {pending.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Awaiting your review ({pending.length})
          </Typography>

          <Stack spacing={2}>
            {pending.map((draft) => {
              const body = bodyOf(draft);
              const stillBlank = body.includes(DRAFT_BLANK);
              const dirty = body !== draft.MessageDraft_Body;
              const busy = busyGuid === draft.MessageDraft_guid;

              return (
                <Card variant="outlined" key={draft.MessageDraft_guid}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {draft.MessageDraft_TemplateKey}
                    </Typography>

                    {draft.MessageDraft_Gaps.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Fill these in before sending</AlertTitle>
                        <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                          {draft.MessageDraft_Gaps.map((gap) => (
                            <li key={gap}>
                              <Typography variant="body2">{gap}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    <TextField
                      multiline
                      fullWidth
                      minRows={6}
                      value={body}
                      disabled={busy}
                      onChange={(e) =>
                        setEdits((prev) => ({
                          ...prev,
                          [draft.MessageDraft_guid]: e.target.value,
                        }))
                      }
                    />

                    {stillBlank && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                        This draft still contains {DRAFT_BLANK} — the client will see it exactly as
                        written.
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SendIcon />}
                        color={stillBlank ? 'warning' : 'primary'}
                        disabled={busy}
                        onClick={() => handleSend(draft)}
                      >
                        {stillBlank ? 'Send with blanks' : 'Send'}
                      </Button>
                      <Button
                        size="small"
                        disabled={busy || !dirty}
                        onClick={() => handleSave(draft)}
                      >
                        Save changes
                      </Button>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<DeleteOutlineIcon />}
                        disabled={busy}
                        onClick={() => handleDiscard(draft)}
                      >
                        Discard
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </>
      )}

      {/* ---------------------------------------------------------------- history */}
      {resolved.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Previously handled
          </Typography>
          <Stack spacing={0.5}>
            {resolved.map((draft) => (
              <Box
                key={draft.MessageDraft_guid}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Chip
                  size="small"
                  label={draft.MessageDraft_Status === 'SENT' ? 'Sent' : 'Discarded'}
                  color={draft.MessageDraft_Status === 'SENT' ? 'success' : 'default'}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {draft.MessageDraft_TemplateKey}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default CaseReplyDraftsPanel;
